import {
  FieldTemplateType,
  type ParticipantFieldGroupTemplate,
  type ParticipantFieldTemplate,
  type UpdateParticipantFieldTemplateRequest,
} from '@piccolohealth/pbs-common';
import type { SelectOption } from '@piccolohealth/ui';
import { ChakraV3 } from '@piccolohealth/ui';
import { z } from '@piccolohealth/util';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { HookedSubmitButton } from '../../../components/forms/V3HookedSubmitButton';
import { createModal } from '../../../components/generic/Modal';
import { useUpdateParticipantFieldTemplateMutation } from '../../../graphql/hooks/useUpdateParticipantFieldTemplateMutation';
import { useAppContext } from '../../../hooks/useAppContext';
import { zodResolver } from '../../../utils/zod';
import { ParticipantFieldTemplateForm } from './ParticipantFieldTemplateForm';

type FormValues = UpdateParticipantFieldTemplateRequest;

interface Props {
  participantFieldGroupTemplateId: string;
  participantFieldTemplate: ParticipantFieldTemplate;
  fieldGroupTemplates: ParticipantFieldGroupTemplate[];
}

const schema: z.ZodSchema<FormValues> = z.object({
  participantFieldGroupTemplateId: z.string(),
  name: z.string(),
  type: z.enum(FieldTemplateType),
  index: z.number(),
  attributes: z.any(),
});

export const ParticipantFieldTemplateUpdateModal = createModal<Props>((props) => {
  const { participantFieldGroupTemplateId, participantFieldTemplate, fieldGroupTemplates, modal } =
    props;
  const { organization, successToast, errorToast } = useAppContext();
  const { hide, remove, visible } = modal;

  const methods = useForm<FormValues>({
    defaultValues: {
      ...participantFieldTemplate,
      participantFieldGroupTemplateId,
    },
    mode: 'all',
    resolver: zodResolver(schema),
  });

  const mutation = useUpdateParticipantFieldTemplateMutation();

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (values.attributes?.options) {
        values.attributes.options = values.attributes.options.map(
          (option: SelectOption<string>) => ({
            value: option.value,
            label: option.value,
            raw: option.value,
          }),
        );
      }

      await mutation
        .mutateAsync({
          organizationId: organization.id,
          participantFieldTemplateId: participantFieldTemplate.id,
          request: values,
        })
        .then(() => {
          hide();
          successToast('Participant field template updated successfully');
        })
        .catch((err) => {
          errorToast(`Error updating participant field template: ${err.message}`);
        });
    },
    [errorToast, hide, mutation, organization.id, participantFieldTemplate.id, successToast],
  );

  return (
    <ChakraV3.Dialog.Root open={visible} onOpenChange={hide} onExitComplete={remove}>
      <ChakraV3.Dialog.Backdrop />
      <ChakraV3.Dialog.Positioner>
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <ChakraV3.Dialog.Content>
              <ChakraV3.DialogTitleSubtitle
                title='Edit a field'
                subtitle='Please complete the following to proceed'
              />
              <ChakraV3.Dialog.Body>
                <ParticipantFieldTemplateForm fieldGroupTemplates={fieldGroupTemplates} />
              </ChakraV3.Dialog.Body>
              <ChakraV3.Dialog.Footer>
                <ChakraV3.HStack>
                  <ChakraV3.Button onClick={hide} size='sm' variant='subtle'>
                    Close
                  </ChakraV3.Button>
                  <HookedSubmitButton>Save</HookedSubmitButton>
                </ChakraV3.HStack>
              </ChakraV3.Dialog.Footer>
            </ChakraV3.Dialog.Content>
          </form>
        </FormProvider>
      </ChakraV3.Dialog.Positioner>
    </ChakraV3.Dialog.Root>
  );
});
